import {
  INotificationDTO,
  INotificationSerializedTarget,
  IQuoteSerializedDataProps,
  IQuotifyPlanStatusChangedSerializedDataProps,
  IQuotifySerializedDataProps,
  IRenewalAwaitingReviewSerializedDataProps,
  IRenewalBaseSerializedDataProps,
  IRenewalCompletedSerializedDataProps,
  IRenewalCreationSerializedDataProps,
  IRenewalDeletionSerializedDataProps,
  IUserDTO,
  NotificationType,
} from '../../../../services';
import { TRUNCATE_MAX, truncateString } from '../../../../utils/truncateString';

export type NotificationProcessor = (
  data: INotificationDTO,
  recoverNotificationTitle?: (notificationType: NotificationType) => string
) => INotificationProcessor;

interface INotificationProcessor {
  title: string | null;
  clientName: string | null;
  description: string | null;
  createdAt?: string | null;
}

export const processChatMessage: NotificationProcessor = (
  data: INotificationDTO
): INotificationProcessor => {
  const notification = JSON.parse(
    data.jsonSerializedTarget
  ) as INotificationSerializedTarget;
  const account = JSON.parse(notification.jsonSerializedAccount) as IUserDTO;

  return {
    title: `${account.firstName} ${account.lastName} sent a message`,
    clientName: notification.clientChat.client.clientName,
    description: truncateString(notification.text, TRUNCATE_MAX.NOTIFICATION),
    createdAt: data.createdAt,
  };
};

export const processRenewalCreation: NotificationProcessor = (
  data: INotificationDTO
): INotificationProcessor => {
  const notification = JSON.parse(
    data.jsonSerializedTarget
  ) as IRenewalCreationSerializedDataProps;

  return {
    title: `${notification.createdByUser} has created a new renewal`,
    clientName: notification.clientName,
    description: `'${notification.renewalName}' was created with ${notification.fileAmount} pending files`,
    createdAt: data.createdAt,
  };
};

export const processRenewalDelete: NotificationProcessor = (
  data: INotificationDTO,
  recoverNotificationTitle?: (notificationType: NotificationType) => string
): INotificationProcessor => {
  const notification = JSON.parse(
    data.jsonSerializedTarget
  ) as IRenewalDeletionSerializedDataProps;

  if (!recoverNotificationTitle) {
    return processUnknown(data);
  }

  return {
    title: `${notification.deletedByUser} has ${recoverNotificationTitle(
      data.type
    )}`,
    clientName: notification.clientName,
    description: `'${notification.renewalName}' was deleted`,
    createdAt: data.createdAt,
  };
};

export const processRenewalUpdate: NotificationProcessor = (
  data: INotificationDTO,
  recoverNotificationTitle?: (notificationType: NotificationType) => string
): INotificationProcessor => {
  const notification = JSON.parse(
    data.jsonSerializedTarget
  ) as IRenewalBaseSerializedDataProps;

  if (!recoverNotificationTitle) {
    return processUnknown(data);
  }

  return {
    title: `'${notification.renewalName}' was updated`,
    clientName: notification.clientName,
    description: `${notification.createdByUser} ${recoverNotificationTitle(data.type)}`,
    createdAt: data.createdAt,
  };
};

export const processQuoteUploaded: NotificationProcessor = (
  data: INotificationDTO
): INotificationProcessor => {
  const notification = JSON.parse(
    data.jsonSerializedTarget
  ) as IQuoteSerializedDataProps;

  return {
    title: `${notification.triggeredByUser} has uploaded a new quote`,
    clientName: notification.clientName,
    description: `'${notification.quoteName}' was uploaded`,
  };
};

export const processQuoteDecisionsMade: NotificationProcessor = (
  data: INotificationDTO
): INotificationProcessor => {
  const notification = JSON.parse(
    data.jsonSerializedTarget
  ) as IQuoteSerializedDataProps;

  return {
    title: `${notification.triggeredByUser} has finished making decisions on a quote`,
    clientName: notification.clientName,
    description: `'${notification.quoteName}' has been reviewed and all decisions have been made`,
  };
};

export const processContributionsSet: NotificationProcessor = (
  data: INotificationDTO
): INotificationProcessor => {
  const notification = JSON.parse(
    data.jsonSerializedTarget
  ) as IQuoteSerializedDataProps;

  return {
    title: `${notification.triggeredByUser} has finished setting contributions for a quote`,
    clientName: notification.clientName,
    description: `'${notification.quoteName}' has been reviewed and all contributions have been set`,
  };
};

export const processRenewalAwaitingReview: NotificationProcessor = (
  data: INotificationDTO
): INotificationProcessor => {
  const notification = JSON.parse(
    data.jsonSerializedTarget
  ) as IRenewalAwaitingReviewSerializedDataProps;

  return {
    title: `${notification.triggeredByUser} has finished uploading all renewal files`,
    clientName: notification.clientName,
    description: `All files for the '${notification.renewalName}' renewal have been uploaded and are awaiting review`,
  };
};

export const processRenewalCompleted: NotificationProcessor = (
  data: INotificationDTO
): INotificationProcessor => {
  const notification = JSON.parse(
    data.jsonSerializedTarget
  ) as IRenewalCompletedSerializedDataProps;

  return {
    title: `${notification.triggeredByUser} has completed a Renewal`,
    clientName: notification.clientName,
    description: `All files for the '${notification.renewalName}' renewal have been approved and the Renewal is now completed. The auto build process is ready to start`,
  };
};

export const processQuotifyFileUploaded: NotificationProcessor = (
  data: INotificationDTO
): INotificationProcessor => {
  const notification = JSON.parse(
    data.jsonSerializedTarget
  ) as IQuotifySerializedDataProps;

  const actor = notification.triggeredByUser ?? 'System';

  return {
    title: `${actor} uploaded ${notification.fileCount} file(s) to workbook "${notification.workbookName}"`,
    clientName: notification.clientName,
    description: `Files submitted for processing`,
    createdAt: data.createdAt,
  };
};

export const processQuotifyFileProcessed: NotificationProcessor = (
  data: INotificationDTO
): INotificationProcessor => {
  const notification = JSON.parse(
    data.jsonSerializedTarget
  ) as IQuotifySerializedDataProps;

  return {
    title: `${notification.fileCount} file(s) in workbook "${notification.workbookName}" finished processing successfully`,
    clientName: notification.clientName,
    description: `Data is ready for review`,
    createdAt: data.createdAt,
  };
};

export const processQuotifyFileFailed: NotificationProcessor = (
  data: INotificationDTO
): INotificationProcessor => {
  const notification = JSON.parse(
    data.jsonSerializedTarget
  ) as IQuotifySerializedDataProps;

  return {
    title: `${notification.fileCount} file(s) in workbook "${notification.workbookName}" failed processing`,
    clientName: notification.clientName,
    description: `Files were moved to Deprecated`,
    createdAt: data.createdAt,
  };
};

export const processQuotifyPlanStatusChanged: NotificationProcessor = (
  data: INotificationDTO
): INotificationProcessor => {
  const notification = JSON.parse(
    data.jsonSerializedTarget
  ) as IQuotifyPlanStatusChangedSerializedDataProps;

  return {
    title: `Plan status changed to "${notification.newStatusLabel}" in workbook "${notification.workbookName}"`,
    clientName: notification.clientName,
    description: `Quote plan #${notification.quotePlanId} is now ${notification.newStatusLabel}`,
    createdAt: data.createdAt,
  };
};

export const processUnknown: NotificationProcessor = (
  data: INotificationDTO
): INotificationProcessor => ({
  title: 'Unknown notification',
  clientName: null,
  description: 'Contact the support',
  createdAt: data.createdAt,
});
